import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Text, Anchor,
} from 'grommet';
import uniqid from 'uniqid';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  DynamicDetailsModal, Spinning,
} from '@Components/Control';
import { NoDataPlaceholder } from '../../SharedComponents';
import { StyledRaisedContainer } from '../../StyledSharedComponents';

const ClaimDetails = ({
  small,
  loading,
  claim,
}) => {
  const { theme: { page, button } } = useAppTheme();

  if (loading) {
    return (
      <Box flex align="center" justify="center">
        <Spinning size="large" color={button.primaryBg} />
      </Box>
    );
  }

  if (!loading && !claim) {
    return (
      <NoDataPlaceholder
        loading={false}
        label="Claim could not be found."
        textColor={page.primaryText}
        loaderColor={button.primaryBg}
      />
    );
  }

  const leftInfo = [
    { label: 'Claim ID', value: claim.id },
    { label: 'Asset ID', value: claim.assetId },
    { label: 'Status', value: Case.capital(claim.status) },
    { label: 'Match policy', value: claim.policy?.name },
    { label: 'Content type', value: Case.capital(claim.contentType) },
    { label: 'UGC type', value: Case.capital(claim.ugcType) },
    { label: 'Created', value: buildDateTimeLabel(claim.timeCreated) },
    { label: 'Last modified', value: buildDateTimeLabel(claim.timeStatusLastModified) },
    {
      label: 'Video link',
      value: (
        <Anchor
          href={`https://www.youtube.com/watch?v=${claim.videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          color={page.altHighlight}
          style={{ fontWeight: 600 }}
        >
          Open on YouTube
        </Anchor>
      ),
    },
    { label: 'Block outside ownership', value: claim.blockOutsideOwnership ? 'Yes' : 'No' },
    { label: 'Origin', value: Case.title(claim.origin?.source) },
  ].filter(Boolean);

  const { matchInfo } = claim;
  const matchSegments = matchInfo?.matchSegments || [];

  // Helper: get value by snake_case or camelCase
  const getKey = (obj, key) => obj?.[key] ?? obj?.[
    key.replace(/([A-Z])/g, '_$1').toLowerCase()
  ] ?? obj?.[
    key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
  ];

  // Helper: convert ms to seconds
  const msToSec = (ms) => Math.floor(parseInt(ms, 10) / 1000);

  // Helper: format seconds to hh:mm:ss
  const formatSeconds = (seconds) => {
    const s = parseInt(seconds, 10);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  // Reusable renderer for match segment modal contents
  const renderSegmentContents = (segment) => {
    if (!segment) return null;

    // Try all possible segment keys
    const videoSegment = getKey(segment, 'videoSegment') || getKey(segment, 'video_segment');
    const referenceSegment = getKey(segment, 'referenceSegment') || getKey(segment, 'reference_segment');
    const manualSegment = getKey(segment, 'manualSegment') || getKey(segment, 'manual_segment');
    const channel = getKey(segment, 'channel');

    // Helper to render a segment block
    const renderBlock = (seg, label, linkable) => {
      if (!seg) return null;
      const startMs = getKey(seg, 'start');
      const durationMs = getKey(seg, 'duration');
      if (startMs == null || durationMs == null) return null;
      const startSec = msToSec(startMs);
      const durationSec = msToSec(durationMs);
      const endSec = startSec + durationSec;

      return (
        <Box direction="column" gap="0.5rem">
          <Text color={page.primaryText} weight={600}>
            {label}
          </Text>
          <Box direction="row" gap="0.5rem" align="center">
            <Text color={page.primaryText} weight={500}>Start:</Text>
            <Text color={page.primaryText}>
              {formatSeconds(startSec)}
              {' '}
              (
              {startSec}
              sec)
            </Text>
            {linkable && claim.videoId && (
              <Anchor
                href={`https://www.youtube.com/watch?v=${claim.videoId}&t=${startSec}`}
                target="_blank"
                rel="noopener noreferrer"
                color={button.primaryBg}
                style={{ fontWeight: 600 }}
              >
                Go to time
              </Anchor>
            )}
          </Box>
          <Box direction="row" gap="0.5rem" align="center">
            <Text color={page.primaryText} weight={500}>Duration:</Text>
            <Text color={page.primaryText}>
              {formatSeconds(durationSec)}
              {' '}
              (
              {durationSec}
              sec)
            </Text>
          </Box>
          <Box direction="row" gap="0.5rem" align="center">
            <Text color={page.primaryText} weight={500}>End:</Text>
            <Text color={page.primaryText}>
              {formatSeconds(endSec)}
              {' '}
              (
              {endSec}
              sec)
            </Text>
            {linkable && claim.videoId && (
              <Anchor
                href={`https://www.youtube.com/watch?v=${claim.videoId}&t=${endSec}`}
                target="_blank"
                rel="noopener noreferrer"
                color={button.primaryBg}
                style={{ fontWeight: 600 }}
              >
                Go to end
              </Anchor>
            )}
          </Box>
        </Box>
      );
    };

    return (
      <Box direction="column" gap="1rem" pad={{ vertical: '0.5rem' }}>
        {channel && (
          <Box direction="row" gap="0.5rem" align="center">
            <Text color={page.primaryText} weight={500}>Channel:</Text>
            <Text color={page.primaryText}>{channel}</Text>
          </Box>
        )}
        {videoSegment && renderBlock(videoSegment, 'Video Segment', false)}
        {referenceSegment && renderBlock(referenceSegment, 'Reference Segment', true)}
        {manualSegment && renderBlock(manualSegment, 'Manual Segment', true)}
      </Box>
    );
  };

  return (
    <Box
      direction={small ? 'column' : 'row'}
      gap="2rem"
      width="100%"
      align="start"
    >
      {/* Claim Info */}
      <StyledRaisedContainer
        direction="column"
        gap="0.75rem"
        pad={{ horizontal: '1.5rem', vertical: '1rem' }}
        navBorder={page.tableBorder}
        margin={{ top: '0' }}
        width={small ? '100%' : '50%'}
      >
        <Text size="1.25rem" weight={600} color={page.primaryText}>
          Claim Info
        </Text>
        <Box direction="column" gap="1rem">
          {leftInfo.map(({ label, value }) => (
            <Box key={label} direction="row" gap="0.5rem" align="center">
              <Text weight={500} color={page.primaryText}>
                {label}
                :
              </Text>
              <Text color={page.primaryText} size="1rem">{value}</Text>
            </Box>
          ))}
        </Box>
      </StyledRaisedContainer>
      {/* Match Info */}
      <StyledRaisedContainer
        direction="column"
        gap="0.75rem"
        pad={{ horizontal: '1.5rem', vertical: '1rem' }}
        navBorder={page.tableBorder}
        margin={{ top: '0' }}
        width={small ? '100%' : '50%'}
      >
        <Text size="1.25rem" weight={600} color={page.primaryText}>
          Match Info
        </Text>
        {!matchInfo ? (
          <Text color={page.hintText}>No match info available.</Text>
        ) : (
          <Box direction="column" gap="0.75rem">
            <Box direction="row" gap="0.5rem" align="center">
              <Text weight={500} color={page.primaryText}>Reference ID:</Text>
              <Text color={page.primaryText}>{matchInfo.referenceId || '—'}</Text>
            </Box>
            {matchInfo.longestMatch && (
              <Box direction="row" gap="0.5rem" align="center">
                <Text weight={500} color={page.primaryText}>Longest:</Text>
                <DynamicDetailsModal
                  tableButtonStyle
                  small={small}
                  label="Longest match details"
                  details={matchInfo.longestMatch}
                />
              </Box>
            )}
            {matchInfo.totalMatch && (
              <Box direction="row" gap="0.5rem" align="center">
                <Text weight={500} color={page.primaryText}>Total:</Text>
                <DynamicDetailsModal
                  tableButtonStyle
                  small={small}
                  label="Total match details"
                  details={matchInfo.totalMatch}
                />
              </Box>
            )}
            {matchSegments.length > 0 && (
              <Box>
                <Text weight={500} color={page.primaryText}>Match segments:</Text>
                <Box direction="column" gap="0.75rem" pad={{ top: '0.75rem' }}>
                  {matchSegments.map((seg, idx) => (
                    <DynamicDetailsModal
                      tableButtonStyle
                      key={uniqid()}
                      small={small}
                      label={`Segment ${idx + 1} (${seg.channel || 'Unknown'})`}
                    >
                      {renderSegmentContents(seg)}
                    </DynamicDetailsModal>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </StyledRaisedContainer>
    </Box>
  );
};

ClaimDetails.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  claim: PropTypes.shape({
    kind: PropTypes.string,
    id: PropTypes.string.isRequired,
    assetId: PropTypes.string,
    videoId: PropTypes.string,
    videoTitle: PropTypes.string,
    videoViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    policy: PropTypes.object,
    appliedPolicy: PropTypes.object,
    status: PropTypes.string,
    contentType: PropTypes.string,
    ugcType: PropTypes.string,
    timeCreated: PropTypes.string,
    timeStatusLastModified: PropTypes.string,
    blockOutsideOwnership: PropTypes.bool,
    matchInfo: PropTypes.shape({
      referenceId: PropTypes.string,
      longestMatch: PropTypes.object,
      totalMatch: PropTypes.object,
      matchSegments: PropTypes.arrayOf(PropTypes.object),
    }),
    origin: PropTypes.shape({
      source: PropTypes.string,
    }),
    isPartnerUploaded: PropTypes.bool,
    studioInfo: PropTypes.shape({
      claimUrl: PropTypes.string,
      videoUrl: PropTypes.string,
    }),
  }),
};

export default ClaimDetails;
